import {
  CloudDoneOutlined,
  CloudUploadOutlined,
  HourglassEmptyOutlined,
  Upload,
} from '@/constants/hardfin-standard-icons';
import { formatDuration } from '@/utilities/format-duration';
import { formatNumber } from '@/utilities/number';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  DialogActions,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

// Per-operation cost in seconds, derived from local benchmarks (TestMoveBenchmark)
const ASSET_UPDATE_SEC = 0.05;
const ACCOUNTING_SEC = 0.026;
const SALE_SEC = 0.03;
const SCRAP_SEC = 0.03;
const PROJECT_SEC = 0.03;
const MOVE_SEC = 0.025;

const MAX_UPLOAD_SECONDS = 600; // 10 minutes

// Column groups that trigger each operation
const ASSET_UPDATE_COLUMNS = [
  'initial location',
  'status',
  'description',
  'inventory date',
];
const ACCOUNTING_COLUMNS = [
  'simple cost basis',
  'bill of materials',
  'direct labor',
  'allocated indirect',
  'freight inbound',
  'freight outbound',
  'installation',
  'tax',
  'tariffs',
  'interest',
  'salvage value',
  'useful life',
  'in service date',
];
const SALE_COLUMNS = ['sale date', 'sale price', 'sale customer'];
const SCRAP_COLUMNS = ['scrap date', 'scrap reason', 'scrap value'];
const PROJECT_COLUMNS = [
  'current project name',
  'assignment date on current project',
  'current project customer',
];
const MOVE_COLUMNS = [
  'current location name',
  'current location customer',
  'location arrival date (delivery)',
  'current location address',
];

function hasAnyColumn(headers: string[], columns: string[]): boolean {
  return columns.some((col) => headers.includes(col));
}

function estimateUploadSeconds(rowCount: number, headers: string[]): number {
  let perRowSec = 0;

  if (hasAnyColumn(headers, ASSET_UPDATE_COLUMNS)) {
    perRowSec += ASSET_UPDATE_SEC;
  }
  if (hasAnyColumn(headers, ACCOUNTING_COLUMNS)) {
    perRowSec += ACCOUNTING_SEC;
  }
  if (hasAnyColumn(headers, SALE_COLUMNS)) {
    perRowSec += SALE_SEC;
  }
  if (hasAnyColumn(headers, SCRAP_COLUMNS)) {
    perRowSec += SCRAP_SEC;
  }
  if (hasAnyColumn(headers, PROJECT_COLUMNS)) {
    perRowSec += PROJECT_SEC;
  }
  if (hasAnyColumn(headers, MOVE_COLUMNS)) {
    perRowSec += MOVE_SEC;
  }

  // Fallback if no recognized columns (shouldn't happen)
  if (perRowSec === 0) {
    perRowSec = 0.05;
  }

  return Math.round(2 + rowCount * perRowSec);
}

interface CsvUploadProps {
  uploadErrorMessage: string | null;
  file: File | null;
  isUploading: boolean;
  onSelectFile: () => void;
  onUpload: () => void;
  onBack: () => void;
  onClose?: () => void;
  hasFatalError?: boolean;
  uploadLineCount?: number;
  uploadHeaders: string[];
}

export function CsvUpload(props: CsvUploadProps) {
  const estimatedUploadTime =
    props.uploadLineCount && props.uploadHeaders.length > 0
      ? estimateUploadSeconds(props.uploadLineCount, props.uploadHeaders)
      : 0;

  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleUpload = () => {
    props.onUpload();
    setCountdown(estimatedUploadTime);
  };

  useEffect(() => {
    if (props.isUploading && countdown > 0 && !timerRef.current) {
      timerRef.current = setInterval(() => {
        setCountdown((prevCount) => prevCount - 1);
      }, 1000);
    }

    if (!props.isUploading) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        setCountdown(0);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [props.isUploading, countdown]);

  return (
    <Card>
      <CardContent>
        {props.uploadErrorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {props.uploadErrorMessage}
          </Alert>
        )}

        {props.hasFatalError ? (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Upload failed</AlertTitle>
            Something went wrong with your upload, support has been notified.
          </Alert>
        ) : (
          <>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'grey.300',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                mb: 3,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'grey.50',
                },
              }}
              onClick={props.onSelectFile}
            >
              {props.file ? (
                <CloudDoneOutlined
                  sx={{ fontSize: 48, color: 'action.active', mb: 2 }}
                />
              ) : (
                <CloudUploadOutlined
                  sx={{ fontSize: 48, color: 'action.active', mb: 2 }}
                />
              )}
              <Typography variant="h6" gutterBottom>
                {props.file
                  ? props.file.name
                  : 'Upload a CSV file with your asset data'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {props.file
                  ? `Ready to upload ${formatNumber(props.uploadLineCount ?? 0)} assets (${formatNumber(Math.round(props.file.size / 1024))} KB)`
                  : 'Click to browse for your CSV file'}
              </Typography>
            </Box>

            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>Warning about asset data updates</AlertTitle>
              <ul
                style={{
                  paddingLeft: '1.5rem',
                  marginTop: '1rem',
                  marginBottom: '0.5rem',
                }}
              >
                <li>Only include serials and fields you want to update</li>
                <li>Empty cells will clear existing data for those fields</li>
                <li>
                  Use separate uploads for different batches of asset data
                  updates
                </li>
              </ul>
            </Alert>
          </>
        )}

        {estimatedUploadTime >= MAX_UPLOAD_SECONDS && !props.isUploading && (
          <Alert severity="error" sx={{ mb: 2, width: '50%', mx: 'auto' }}>
            <AlertTitle>File is too large to upload</AlertTitle>
            This file will take too long to process. Please break it into
            smaller files and upload them separately
          </Alert>
        )}
        {estimatedUploadTime >= 60 &&
          estimatedUploadTime < MAX_UPLOAD_SECONDS &&
          !props.isUploading && (
            <Alert severity="warning" sx={{ mb: 2, width: '50%', mx: 'auto' }}>
              <AlertTitle>This data looks large</AlertTitle>
              Consider breaking the file into smaller pieces for reliability
            </Alert>
          )}

        <Box
          display="flex"
          justifyContent="space-between"
          alignContent={'center'}
        >
          <DialogActions>
            <Button
              variant="outlined"
              onClick={props.onBack}
              disabled={props.isUploading}
            >
              Back
            </Button>
          </DialogActions>
          <Box display="flex" alignItems={'center'}>
            {props.uploadLineCount && !props.isUploading && (
              <Typography color={'text.secondary'}>
                Estimated processing time: {formatDuration(estimatedUploadTime)}
              </Typography>
            )}
            {props.isUploading && countdown > 0 && (
              <Box display="flex" alignItems="center" gap={1}>
                <HourglassEmptyOutlined
                  sx={{
                    color: 'text.secondary',
                    fontSize: 20,
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': {
                        opacity: 0.4,
                      },
                      '50%': {
                        opacity: 1,
                      },
                    },
                  }}
                />
                <Typography color={'text.secondary'}>
                  Estimated time remaining: {formatDuration(countdown)}
                </Typography>
              </Box>
            )}
            {props.isUploading && countdown === 0 && (
              <Typography color={'text.secondary'}>
                Almost finished...
              </Typography>
            )}
          </Box>
          <DialogActions>
            {props.hasFatalError && props.onClose ? (
              <Button variant="contained" onClick={props.onClose}>
                Close
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={
                  !props.file ||
                  props.isUploading ||
                  estimatedUploadTime >= MAX_UPLOAD_SECONDS
                }
                startIcon={
                  props.isUploading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Upload />
                  )
                }
              >
                {props.isUploading ? 'Uploading...' : 'Upload CSV'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </CardContent>
    </Card>
  );
}
